import { Delete, ExpandMore, LocalShippingOutlined } from "@mui/icons-material";
import EditIcon from '@mui/icons-material/Edit';
import {
  AccordionSummary,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { titleize } from "inflection";
import { useContext, useState } from "react";

import EditDeliveryModal from '../EditDeliveryModal';
import { useMutation } from "@apollo/client/react";
import dayjs from "dayjs";
import RequestPermissionTooltip from "@/components/RequestPermissionTooltip";
import { DeliveryAndCollectionItemStateEnum, DeliveryAndCollectionRegisterItem, DeliveryAndCollectionItemState, DestroyDeliveryAndCollectionItemDocument, PermissionActionEnum, PermissionEntityEnum } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import PermissionContext from "@/Contexts/Permissions";
import CurrentUserContext from "@/Contexts/CurrentUser";

const colors = {
  ready_for_collection: {
    bg: "#fff8e1",
    color: "#f57f17",
    border: "#ffecb3",
  },
  arrived: { bg: "#e3f2fd", color: "#1565c0", border: "#bbdefb" },
  deployed: { bg: "#e8f5e9", color: "#2e7d32", border: "#c8e6c9" },
  collected: { bg: "#e8f5e9", color: "#2e7d32", border: "#c8e6c9" },
};
const getStatusColors = (status: DeliveryAndCollectionItemStateEnum) => {
  return (
    colors[status] || { bg: "#f5f5f5", color: "#616161", border: "#e0e0e0" }
  );
};

interface DeliveryCollectionCardHeaderProps {
  deliveryItem: DeliveryAndCollectionRegisterItem;
  itemState: DeliveryAndCollectionItemState;
  onItemAction: (action: "deploy" | "collect", itemState: DeliveryAndCollectionItemState) => void;
}

const DeliveryCollectionCardHeader: React.FC<
  DeliveryCollectionCardHeaderProps
> = ({ deliveryItem, itemState, onItemAction }) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const [showEditModal, setShowEditModal] = useState(false);

  const [destroyDeliveryAndCollectionItem] = useMutation(
    DestroyDeliveryAndCollectionItemDocument,
    {
      onCompleted: () => {
        displayNotification({
          text: "Delivery And Collection Item deleted successfully",
          type: "success",
        });
      },
      onError: (error) => {
        displayNotification({ text: error.message, type: "error" });
      },
    }
  );

  const handleDeleteItem = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string
  ) => {
    event.stopPropagation();
    destroyDeliveryAndCollectionItem({
      variables: { id },
      update(cache) {
        cache.evict({
          id: cache.identify({ __typename: "DeliveryAndCollectionRegisterItem", id }),
        });
        cache.gc();
      },
    });
  };

  const handleEditClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setShowEditModal(true);
  };

  const isStateArrived = itemState.currentState === DeliveryAndCollectionItemStateEnum.Arrived;
  const canDelete = canPerformAction(PermissionActionEnum.Manage, PermissionEntityEnum.Scheduling);
  const canEdit = canPerformAction(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling);

  return (
    <>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls={`panel${deliveryItem.id}-content`}
        id={`panel${deliveryItem.id}-header`}
        sx={{ background: colors[itemState.currentState].bg }}
      >
        <Grid
          container
          alignItems="center"
          paddingX={2}
          paddingY={1}
          justifyContent="space-between"
          size={12}>
          <Grid size="grow">
            <Typography variant="subtitle1" fontWeight={600}>
              {deliveryItem.companyResource.name} {" "}
              {deliveryItem.companyResource?.resourceMeta?.shortCode}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {`${titleize(itemState.currentState)} At: ${dayjs(itemState.currentStateSetAt).format(dateTimePreference.time)}`}
            </Typography>
          </Grid>
          <Grid container justifyContent="flex-end" alignItems="center" size={5}>
            <Chip
              label={titleize(itemState.currentState)}
              size="small"
              sx={{ ...getStatusColors(itemState.currentState) }}
            />
            <RequestPermissionTooltip
              hasPermission={canEdit}
              requiredPermission={{
                action: PermissionActionEnum.Edit,
                entity: PermissionEntityEnum.Scheduling
              }}
              checkRequestExists={checkRequestExists}
              onRequest={requestPermission}
            >
              <span>
                <Tooltip title={canEdit ? "Edit entry" : ""}>
                  <IconButton
                    onClick={handleEditClick}
                    disabled={!(canEdit && isStateArrived)}
                  >
                    <EditIcon color={`${(canEdit && isStateArrived) ? "primary" : "disabled"}`} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={canEdit ? `Mark as ${itemState.currentState === DeliveryAndCollectionItemStateEnum.ReadyForCollection ? "Collected" : "Deployed"}` : ""}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemAction(itemState.currentState === DeliveryAndCollectionItemStateEnum.ReadyForCollection ? "collect" : "deploy", itemState)
                    }}
                    disabled={!canEdit}
                  >
                    <LocalShippingOutlined color={`${canEdit ? "primary" : "disabled"}`} />
                  </IconButton>
                </Tooltip>
              </span>
            </RequestPermissionTooltip>
            <RequestPermissionTooltip
              hasPermission={canDelete}
              requiredPermission={{
                action: PermissionActionEnum.Manage,
                entity: PermissionEntityEnum.Scheduling
              }}
              checkRequestExists={checkRequestExists}
              onRequest={requestPermission}
            >
              <span>
                <Tooltip title={canDelete ? "Remove entry" : ""}>
                  <IconButton
                    onClick={(event) => handleDeleteItem(event, deliveryItem.id)}
                    disabled={!(canDelete && isStateArrived)}
                  >
                    <Delete color={`${(canDelete && isStateArrived) ? "error" : "disabled"}`} />
                  </IconButton>
                </Tooltip>
              </span>
            </RequestPermissionTooltip>
          </Grid>
        </Grid>
      </AccordionSummary>

      {showEditModal && (
        <EditDeliveryModal
          handleClose={() => setShowEditModal(false)}
          deliveryItem={deliveryItem}
          itemState={itemState}
        />
      )}
    </>
  );
};

export default DeliveryCollectionCardHeader;
